import { Navigo__SideNavbarSectionControllerIdentifier } from "controllers/identifiers";
import BaseController from "confinus_ui/base_controller";
import SideNavbarSectionController from "navigo/side_navbar_section_controller";

const HOVER_BODY_CLASS = "side-navbar-collapsed-hover-open";
const COLLAPSED_BODY_CLASS = "side-navbar-collapsed";
const EXPANDED_BODY_CLASS = "side-navbar-expanded";

export const SIDE_NAVBAR_MOBILE_TOGGLE_EVENT =
  "navigo--side-navbar:toggle-mobile-overlay";

export default class SideNavbarController extends BaseController {
  public static outlets = [Navigo__SideNavbarSectionControllerIdentifier];
  declare private readonly navigoSideNavbarSectionOutlets: SideNavbarSectionController[];
  private get sectionControllers() {
    return this.navigoSideNavbarSectionOutlets;
  }

  public static targets = [
    "searchNavigation",
    "mobileSearchNavigation",
    "desktopMenu",
    "mobileMenu",
    "mobileMenuOverlay",
    "mobileMenuCanvas",
    "mobileMenuCloseButton",
    "desktopCollapseIcon",
    "desktopExpandIcon",
    "desktopLogo",
    "desktopAvatarLogo",
  ];

  public static values = {
    collapsed: Boolean,
  };

  declare private readonly collapsedValue: boolean;

  declare private readonly searchNavigationTarget: HTMLInputElement;
  declare private readonly mobileSearchNavigationTarget: HTMLInputElement;
  declare private readonly desktopMenuTarget: HTMLDivElement;
  declare private readonly mobileMenuTarget: HTMLDivElement;
  declare private readonly mobileMenuOverlayTarget: HTMLDivElement;
  declare private readonly mobileMenuCanvasTarget: HTMLDivElement;
  declare private readonly mobileMenuCloseButtonTarget: HTMLButtonElement;
  declare private readonly desktopCollapseIconTarget: HTMLDivElement;
  declare private readonly desktopExpandIconTarget: HTMLDivElement;
  declare private readonly desktopLogoTarget: HTMLDivElement;
  declare private readonly desktopAvatarLogoTarget: HTMLDivElement;

  public static classes = [
    "mobileMenuOverlayEntering",
    "mobileMenuOverlayEnteringFrom",
    "mobileMenuOverlayEnteringTo",
    "mobileMenuOverlayLeaving",
    "mobileMenuOverlayLeavingFrom",
    "mobileMenuOverlayLeavingTo",
    "mobileMenuCanvasEntering",
    "mobileMenuCanvasEnteringFrom",
    "mobileMenuCanvasEnteringTo",
    "mobileMenuCanvasLeaving",
    "mobileMenuCanvasLeavingFrom",
    "mobileMenuCanvasLeavingTo",
    "mobileMenuCloseButtonEntering",
    "mobileMenuCloseButtonEnteringFrom",
    "mobileMenuCloseButtonEnteringTo",
    "mobileMenuCloseButtonLeaving",
    "mobileMenuCloseButtonLeavingFrom",
    "mobileMenuCloseButtonLeavingTo",
  ];
  declare protected readonly mobileMenuOverlayEnteringClasses: string[];
  declare protected readonly mobileMenuOverlayEnteringFromClasses: string[];
  declare protected readonly mobileMenuOverlayEnteringToClasses: string[];
  declare protected readonly mobileMenuOverlayLeavingClasses: string[];
  declare protected readonly mobileMenuOverlayLeavingFromClasses: string[];
  declare protected readonly mobileMenuOverlayLeavingToClasses: string[];
  declare protected readonly mobileMenuCanvasEnteringClasses: string[];
  declare protected readonly mobileMenuCanvasEnteringFromClasses: string[];
  declare protected readonly mobileMenuCanvasEnteringToClasses: string[];
  declare protected readonly mobileMenuCanvasLeavingClasses: string[];
  declare protected readonly mobileMenuCanvasLeavingFromClasses: string[];
  declare protected readonly mobileMenuCanvasLeavingToClasses: string[];
  declare protected readonly mobileMenuCloseButtonEnteringClasses: string[];
  declare protected readonly mobileMenuCloseButtonEnteringFromClasses: string[];
  declare protected readonly mobileMenuCloseButtonEnteringToClasses: string[];
  declare protected readonly mobileMenuCloseButtonLeavingClasses: string[];
  declare protected readonly mobileMenuCloseButtonLeavingFromClasses: string[];
  declare protected readonly mobileMenuCloseButtonLeavingToClasses: string[];

  private debounceSearch?: any = undefined;
  private debounceMouseOver?: any = undefined;
  private collapsed: boolean = false;
  private overlayOpen: boolean = false;
  private readonly COOKIE_KEY = "sidebar_collapsed";

  public connect() {
    super.connect();
    this.collapsed = this.collapsedValue;
    this.animateDesktopMenu();
    this.updateCookie(this.collapsed);
  }

  // Helper to set the cookie with appropriate params
  private updateCookie(collapsed: boolean): void {
    document.cookie = `${this.COOKIE_KEY}=${collapsed}; path=/; max-age=31536000; SameSite=Lax`;
  }

  public toggleMobileMenu() {
    // todo toggle the mobile view
    this.overlayOpen = !this.overlayOpen;
    this.toggleMobileOverlay(this.overlayOpen);
    // Clear search
    this.searchNavigationTarget.value = "";
    this.mobileSearchNavigationTarget.value = "";
    this.search();
  }

  public handleMouseOver() {
    if (this.documentBody.classList.contains(COLLAPSED_BODY_CLASS)) {
      if (this.debounceMouseOver) {
        clearTimeout(this.debounceMouseOver);
      }
      this.debounceMouseOver = setTimeout(() => {
        this.debounceMouseOver = undefined;

        this.toggleLogo(false);
        this.documentBody.classList.add(HOVER_BODY_CLASS);
      }, 500);
    }
  }

  public handleMouseAway() {
    if (this.debounceMouseOver) {
      clearTimeout(this.debounceMouseOver);
    }
    if (this.documentBody.classList.contains(COLLAPSED_BODY_CLASS)) {
      this.toggleLogo(true);
      this.documentBody.classList.remove(HOVER_BODY_CLASS);
    }
  }

  public search() {
    const val =
      this.searchNavigationTarget.value.trim().toLowerCase() ||
      this.mobileSearchNavigationTarget.value.trim().toLowerCase();

    if (this.debounceSearch) {
      clearTimeout(this.debounceSearch);
    }
    this.debounceSearch = setTimeout(() => {
      this.debounceSearch = undefined;
      this.sectionControllers.forEach((section) => {
        section.filterOnSearch(val);
      });
    }, 300);
  }

  public toggleCollapseDesktopMenu() {
    this.collapsed = !this.collapsed;
    this.animateDesktopMenu();
    this.updateCookie(this.collapsed);
  }

  private animateDesktopMenu() {
    this.documentBody.classList.remove(HOVER_BODY_CLASS);
    this.documentBody.classList.remove(EXPANDED_BODY_CLASS);
    this.documentBody.classList.remove(COLLAPSED_BODY_CLASS);
    if (this.collapsed) {
      this.documentBody.classList.add(COLLAPSED_BODY_CLASS);
    } else {
      this.documentBody.classList.add(EXPANDED_BODY_CLASS);
    }
    this.toggleLogo(this.collapsed);
    this.toggleTargetElementClasses(
      this.desktopCollapseIconTarget,
      this.collapsed,
      [],
      ["hidden"],
    );
    this.toggleTargetElementClasses(
      this.desktopExpandIconTarget,
      this.collapsed,
      ["hidden"],
      [],
    );
  }

  private toggleMobileOverlay(open: boolean) {
    this.desktopMenuTarget.classList.add("hidden");
    if (open) {
      this.toggleTargetElementClasses(
        this.mobileMenuTarget,
        open,
        ["hidden"],
        [],
      );
    } else {
      setTimeout(() => {
        this.toggleTargetElementClasses(
          this.mobileMenuTarget,
          open,
          ["hidden"],
          [],
        );
      }, 300);
    }
    this.toggleTargetElementTransitionClasses(
      this.mobileMenuOverlayTarget,
      open,
      this.mobileMenuOverlayEnteringClasses,
      this.mobileMenuOverlayEnteringFromClasses,
      this.mobileMenuOverlayEnteringToClasses,
      this.mobileMenuOverlayLeavingClasses,
      this.mobileMenuOverlayLeavingFromClasses,
      this.mobileMenuOverlayLeavingToClasses,
    );
    this.toggleTargetElementTransitionClasses(
      this.mobileMenuCanvasTarget,
      open,
      this.mobileMenuCanvasEnteringClasses,
      this.mobileMenuCanvasEnteringFromClasses,
      this.mobileMenuCanvasEnteringToClasses,
      this.mobileMenuCanvasLeavingClasses,
      this.mobileMenuCanvasLeavingFromClasses,
      this.mobileMenuCanvasLeavingToClasses,
    );
    this.toggleTargetElementTransitionClasses(
      this.mobileMenuCloseButtonTarget,
      open,
      this.mobileMenuCloseButtonEnteringClasses,
      this.mobileMenuCloseButtonEnteringFromClasses,
      this.mobileMenuCloseButtonEnteringToClasses,
      this.mobileMenuCloseButtonLeavingClasses,
      this.mobileMenuCloseButtonLeavingFromClasses,
      this.mobileMenuCloseButtonLeavingToClasses,
    );
  }

  private toggleLogo(collapsed: boolean) {
    this.toggleTargetElementClasses(
      this.desktopLogoTarget,
      collapsed,
      [],
      ["hidden"],
    );
    this.toggleTargetElementClasses(
      this.desktopAvatarLogoTarget,
      collapsed,
      ["hidden"],
      [],
    );
  }

  private get documentBody() {
    return document.getElementsByTagName("body")[0];
  }
}
